import { Palette, FlaskConical, BadgeCheck, Megaphone, ArrowRight, LucideIcon } from 'lucide-react';
import Link from 'next/link';
import { Typography } from '@/components/ui/Typography';
import { Section } from '@/components/ui/Section';
import { motion } from 'framer-motion';

export interface ServiceItem {
    showMedia?: boolean;
    mediaType?: "icon" | "image";
    mediaIcon?: string;
    mediaImage?: string;
    title: string;
    description: string;
    showButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    slug?: string;
}

export interface ServicesGridProps {
    id?: string;
    badgeText?: string;
    heading?: string;
    subheading?: string;
    services?: ServiceItem[];
    useDesignSystem?: boolean;
}

const iconMap: Record<string, LucideIcon> = {
    Palette: Palette,
    FlaskConical: FlaskConical,
    BadgeCheck: BadgeCheck,
    Megaphone: Megaphone,
};

export default function ServicesGrid({
    id,
    badgeText = "Our Capability",
    heading = "Expert Solutions for Your Brand",
    subheading = "We provide comprehensive, end-to-end manufacturing services to help you build a world-class personal care brand.",
    useDesignSystem = true,
    services = [
        {
            title: 'Label & Packaging Designing',
            description: 'Label and packaging designing is an essential aspect of branding and marketing strategy.',
            showMedia: true,
            mediaType: "icon",
            mediaIcon: 'Palette',
            slug: 'label-packaging-designing',
        },
        {
            title: 'Customised Finished Product',
            description: 'A personal care product design must account for market demand.',
            showMedia: true,
            mediaType: "icon",
            mediaIcon: 'FlaskConical',
            slug: 'customised-finished-product',
        },
        {
            title: 'Trademark & Logo',
            description: 'We create trademarks and logos that effectively represent your brand.',
            showMedia: true,
            mediaType: "icon",
            mediaIcon: 'BadgeCheck',
            slug: 'trademark-logo',
        },
        {
            title: 'Digital Marketing',
            description: 'We help brands promote their products and services to their target audience.',
            showMedia: true,
            mediaType: "icon",
            mediaIcon: 'Megaphone',
            slug: 'digital-marketing',
        },
    ],
    ...pProps
}: ServicesGridProps & Record<string, any>) {
    const sectionId = id || (pProps as any).id;
    const dataBlock = (pProps as any)["data-block"];

    return (
        <section id={sectionId} data-block={dataBlock} className={`pt-0 pb-12 sm:pt-0 sm:pb-24 relative overflow-hidden ${useDesignSystem ? 'bg-white' : 'bg-slate-50'}`}>
            {useDesignSystem && (
                <>
                    <div className="absolute top-0 left-1/4 w-96 h-96 bg-nb-green/5 rounded-full blur-[120px] -translate-y-1/2 pointer-events-none" />
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-nb-green-light/5 rounded-full blur-[120px] translate-y-1/2 pointer-events-none" />
                </>
            )}

            <div className="max-w-full mx-auto px-6 sm:px-8 relative z-10">
                {(badgeText || heading || subheading) && (
                    <div className="text-center mb-2 sm:mb-3">
                        {badgeText && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className={`inline-block px-5 py-2 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] leading-none mb-3 sm:mb-4 ${useDesignSystem ? 'bg-nb-green/10 text-nb-green border border-nb-green/20' : 'bg-nb-green/5 text-nb-green'
                                    }`}
                            >
                                {badgeText}
                            </motion.div>
                        )}

                        {useDesignSystem ? (
                            <div className="space-y-3">
                                {heading && (
                                    <div className="text-center mb-2 max-w-2xl mx-auto">
                                        <Typography variant="section-title" align="center" color="slate-900" className="m-0 mt-0">
                                            {heading}
                                        </Typography>
                                        <div className="w-20 h-1 bg-nb-green mx-auto mt-2 rounded-full opacity-20" />
                                    </div>
                                )}
                                {subheading && (
                                    <Typography variant="body" className="text-slate-500 max-w-2xl mx-auto font-medium opacity-80 m-0 mt-0">
                                        {subheading}
                                    </Typography>
                                )}
                            </div>
                        ) : (
                            <>
                                {heading && <h2 className="text-3xl sm:text-5xl md:text-6xl font-black text-slate-900 tracking-tight leading-[1.1]">{heading}</h2>}
                                {subheading && (
                                    <p className="text-base sm:text-xl text-slate-500 max-w-2xl mx-auto mt-4 sm:mt-6 font-medium opacity-80">
                                        {subheading}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                )}

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                    {(services || []).map((service, i) => {
                        const IconComponent = service.mediaIcon ? (iconMap[service.mediaIcon] || null) : null;
                        return (
                            <motion.div
                                key={service.slug || i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className={`group relative rounded-[24px] sm:rounded-[32px] p-5 sm:p-7 transition-all duration-700 flex flex-col h-full transform-gpu ${useDesignSystem
                                        ? 'bg-white border border-slate-100/50 hover:border-nb-green/20 hover:shadow-[0_32px_64px_-16px_rgba(43,238,108,0.15)] hover:-translate-y-2'
                                        : 'bg-white border border-slate-900/5 hover:-translate-y-4 hover:shadow-2xl hover:shadow-nb-green/15'
                                    }`}
                            >
                                {service.showMedia !== false && (
                                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-[16px] flex items-center justify-center mb-4 border transition-all duration-500 overflow-hidden flex-shrink-0 ${useDesignSystem
                                            ? 'bg-slate-50 border-slate-100 group-hover:bg-nb-green group-hover:border-nb-green group-hover:shadow-lg group-hover:shadow-nb-green/30'
                                            : 'bg-gradient-to-br from-nb-green/10 to-nb-green-light/5 border-nb-green/10 group-hover:bg-nb-green'
                                        }`}>
                                        {service.mediaType === "image" && service.mediaImage ? (
                                            <img
                                                src={service.mediaImage.startsWith('http') ? service.mediaImage : `/api/storage/${service.mediaImage}`}
                                                className="w-full h-full object-cover"
                                                alt={service.title}
                                            />
                                        ) : IconComponent ? (
                                            <IconComponent className={`w-6 h-6 sm:w-7 sm:h-7 transition-colors duration-500 ${useDesignSystem ? 'text-slate-400 group-hover:text-white' : 'text-nb-green group-hover:text-white'
                                                }`} />
                                        ) : (
                                            <span className="text-xl sm:text-2xl group-hover:scale-110 transition-transform">
                                                {service.mediaIcon || "✨"}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {useDesignSystem ? (
                                    <div className="space-y-2 flex-grow">
                                        {service.title && (
                                            <Typography variant="h3" color="slate-900" weight="black" className="text-xl sm:text-2xl leading-[1.1] group-hover:text-nb-green transition-colors duration-500">
                                                {service.title}
                                            </Typography>
                                        )}
                                        {service.description && (
                                            <Typography variant="small" className="text-slate-500 leading-relaxed font-medium">
                                                {service.description}
                                            </Typography>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        {service.title && <h3 className="font-black text-xl sm:text-2xl text-slate-900 mb-3 sm:mb-5 leading-tight">{service.title}</h3>}
                                        {service.description && <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-6 sm:mb-10 font-medium opacity-80 flex-grow">{service.description}</p>}
                                    </>
                                )}

                                {service.showButton !== false && (
                                    <Link
                                        href={
                                            service.buttonLink
                                                ? service.buttonLink
                                                : service.slug
                                                    ? `/${service.slug}`
                                                    : "/services"
                                        }
                                        className={`inline-flex items-center gap-2 text-xs sm:text-sm font-black transition-all mt-4 ${useDesignSystem
                                                ? 'text-slate-400 hover:text-nb-green uppercase tracking-widest'
                                                : 'text-nb-green group-hover:text-slate-900'
                                            }`}
                                    >
                                        {service.buttonText || "Explore Service"}
                                        <ArrowRight className={`w-3.5 h-3.5 transition-transform ${useDesignSystem ? 'group-hover:translate-x-1' : 'group-hover:translate-x-2'}`} />
                                    </Link>
                                )}

                                {useDesignSystem && IconComponent && (
                                    <div className="absolute bottom-10 right-10 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-700 pointer-events-none">
                                        <IconComponent className="w-32 h-32 text-slate-900 -rotate-12" />
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

